// src/pages/modules/Admin/FormBuilder/FormBuilder.tsx
import React from 'react'
import {
  Alert, Badge, Button, Card, Col, Form,
  ListGroup, Row, Spinner, Tab, Tabs,
} from 'react-bootstrap'
import { AuthContext, dbRead, dbSet } from '../../../../_services/firebase'
import { DB_REGISTER_FORM, DB_POINT_CONFIG } from '../../../../_config/db_paths'
import { saveLog } from '../../../../_services/firebase'
import { toastDanger, toastSuccess } from '../../../../_utils'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import type { Field, FieldType, Page } from '../../../../_services/formEngine'

// ─── Types ────────────────────────────────────────────────────
interface Threshold { op: '<='; value: number; score: number }
interface FormulaRule { id: string; label?: string; formula: string; thresholds: Threshold[] }
interface TimeRule { dateVar: string; thresholds: Threshold[] }
interface StringRule { varName: string; mapping: { value: string; score: number }[] }
interface NumbRule { varName: string; thresholds: Threshold[] }

// ─── Utils ────────────────────────────────────────────────────
const makeId = (len = 8) => Math.random().toString(36).slice(2, 2 + len)
const toArr  = (v: any): any[] => !v ? [] : Array.isArray(v) ? v : Object.values(v)

const FIELD_TYPES: { value: FieldType; label: string }[] = [
  { value: 'text',     label: 'ข้อความ (text)' },
  { value: 'number',   label: 'ตัวเลข (number)' },
  { value: 'date',     label: 'วันที่ (date)' },
  { value: 'radio',    label: 'ตัวเลือกเดียว (radio)' },
  { value: 'checkbox', label: 'เลือกหลายค่า (checkbox)' },
  { value: 'textarea', label: 'กล่องข้อความ (textarea)' },
  { value: 'select',   label: 'เลือกจากรายการ (select)' },
  { value: 'url',      label: 'ลิงก์ (url)' },
  { value: 'phone',    label: 'เบอร์โทร (phone)' },
]

const isOptionType = (t: FieldType) => t === 'radio' || t === 'checkbox' || t === 'select'

const makeEmptyField  = (type: FieldType): Field => ({ id: makeId(), name: `field_${makeId(4)}`, type, label: '', required: false, scorable: false })
const makeEmptyPage   = (title: string): Page   => ({ id: makeId(), title, fields: [] })
const defaultTh       = (): Threshold[]          => [{ op: '<=', value: 10, score: 50 }, { op: '<=', value: 20, score: 40 }, { op: '<=', value: 30, score: 30 }]


// ─── Default Form Template ────────────────────────────────────
const mk = (name: string, label: string, type: FieldType, opts: Partial<Field> = {}): Field =>
  ({ id: makeId(), name, label, type, required: false, scorable: false, ...opts })

const DEFAULT_PAGES: Page[] = [
  {
    id: makeId(), title: 'ข้อมูลส่วนตัว', fields: [
      mk('fullName',      'ชื่อ - นามสกุล',    'text',   { required: true, scorable: true }),
      mk('nationalId',    'เลขบัตรประชาชน',    'text',   { required: true }),
      mk('birthDate',     'ปี-เดือน-วันเกิด',  'date',   { required: true, scorable: true }),
      mk('age',           'อายุ',               'number', { required: true, scorable: true }),
      mk('phone',         'หมายเลขโทรศัพท์',   'phone',  { required: true }),
      mk('address',       'ที่อยู่',            'text',   { required: true }),
      mk('maritalStatus', 'สถานะภาพ',          'radio',  { required: true, scorable: true, options: [{ label: 'โสด', value: 'โสด' }, { label: 'สมรส', value: 'สมรส' }, { label: 'หย่าร้าง', value: 'หย่าร้าง' }] }),
      mk('facebookLink',  'Link Facebook',      'url'),
      mk('locationLink',  'Link Location',      'url'),
    ]
  },
  {
    id: makeId(), title: 'ข้อมูลการประกอบอาชีพ', fields: [
      mk('company',        'บริษัท/ร้านค้า',     'text'),
      mk('position',       'ตำแหน่งงาน',         'text',   { scorable: true }),
      mk('businessType',   'ประเภทธุรกิจ',        'text'),
      mk('workingPeriod',  'ระยะเวลาทำงาน',       'text',   { scorable: true }),
      mk('workAddress',    'สถานที่ทำงาน',        'text'),
      mk('workLocation',   'Link ที่ทำงาน',       'url'),
      mk('currentAddress', 'ที่อยู่ปัจจุบัน',     'text'),
    ]
  },
  {
    id: makeId(), title: 'ข้อมูลรายรับ รายจ่าย หนี้สิน', fields: [
      mk('dailyIncome',       'รายได้/วัน',         'number', { required: true, scorable: true }),
      mk('otherIncomeDaily',  'รายได้อื่น/วัน',     'number', { scorable: true }),
      mk('debtDaily',         'หนี้สิน/วัน',        'number', { scorable: true }),
      mk('creditCardDaily',   'บัตรเครดิต/วัน',     'number', { scorable: true }),
    ]
  },
  {
    id: makeId(), title: 'ข้อมูลการกู้และวงเงิน', fields: [
      mk('wantedAmount',    'วงเงินที่ต้องการ',   'number', { required: true }),
      mk('installmentDays', 'ผ่อน วัน/เดือน',     'number', { required: true }),
      mk('purpose',         'วัตถุประสงค์',        'text',   { required: true }),
      mk('zone',            'โซน',                 'select', { required: true, multiple: true, options: [{ label: 'A', value: 'A' }, { label: 'B', value: 'B' }, { label: 'C', value: 'C' }] }),
    ]
  },
  {
    id: makeId(), title: 'ข้อมูลเสริม / ผู้ค้ำประกัน', fields: [
      mk('guarantor_fullName',     'ชื่อผู้ค้ำประกัน',      'text'),
      mk('guarantor_phone',        'เบอร์ผู้ค้ำ',           'phone'),
      mk('guarantor_facebook',     'Facebook ผู้ค้ำ',       'url'),
      mk('guarantor_homeAddress',  'ที่อยู่บ้านผู้ค้ำ',     'text'),
      mk('guarantor_shopAddress',  'ที่อยู่ร้านผู้ค้ำ',     'text'),
      mk('guarantor_homeLocation', 'Link บ้านผู้ค้ำ',       'url'),
      mk('guarantor_shopLocation', 'Link ร้านผู้ค้ำ',       'url'),
      mk('guarantor_business',     'ธุรกิจผู้ค้ำ',          'text'),
    ]
  },
]

// ─── ThresholdEditor ──────────────────────────────────────────
function ThresholdEditor({ thresholds, onChange }: { thresholds: Threshold[]; onChange: (t: Threshold[]) => void }) {
  const update = (i: number, patch: Partial<Threshold>) =>
    onChange(thresholds.map((t, idx) => idx === i ? { ...t, ...patch } : t))
  return (
    <div>
      {thresholds.map((t, i) => (
        <div key={i} className='d-flex gap-2 align-items-center mb-1'>
          <span style={{ fontSize: 12, minWidth: 50 }}>ค่า ≤</span>
          <Form.Control size='sm' type='number' style={{ width: 80 }} value={t.value} onChange={(e) => update(i, { value: Number(e.target.value) })} />
          <span style={{ fontSize: 12 }}>→ คะแนน</span>
          <Form.Control size='sm' type='number' style={{ width: 80 }} value={t.score} onChange={(e) => update(i, { score: Number(e.target.value) })} />
          <Button size='sm' variant='outline-danger' style={{ fontSize: 11 }} onClick={() => onChange(thresholds.filter((_, idx) => idx !== i))}>✕</Button>
        </div>
      ))}
      <Button size='sm' variant='outline-primary' style={{ fontSize: 11 }} onClick={() => onChange([...thresholds, { op: '<=', value: 0, score: 0 }])}>+ เพิ่มเงื่อนไข</Button>
    </div>
  )
}

// ─── FieldEditor ──────────────────────────────────────────────
function FieldEditor({ field, onChange, onRemove, onMoveUp, onMoveDown }: {
  field: Field; onChange: (patch: Partial<Field>) => void
  onRemove: () => void; onMoveUp: () => void; onMoveDown: () => void
}) {
  const [optInput, setOptInput] = React.useState('')
  const addOption = () => {
    const v = optInput.trim()
    if (!v) return
    onChange({ options: [...(field.options || []), { label: v, value: v }] })
    setOptInput('')
  }
  return (
    <Card className='mb-2 shadow-sm' style={{ borderLeft: '3px solid #ffc107' }}>
      <Card.Body style={{ padding: '10px 12px' }}>
        <Row className='g-2'>
          <Col xs={12} md={4}>
            <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ชื่อ Field (name)</Form.Label>
            <Form.Control size='sm' value={field.name} onChange={(e) => onChange({ name: e.target.value })} />
          </Col>
          <Col xs={12} md={4}>
            <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Label (แสดงผล)</Form.Label>
            <Form.Control size='sm' value={field.label} onChange={(e) => onChange({ label: e.target.value })} />
          </Col>
          <Col xs={6} md={2}>
            <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ประเภท</Form.Label>
            <Form.Select size='sm' value={field.type} onChange={(e) => onChange({ type: e.target.value as FieldType, options: [] })}>
              {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Form.Select>
          </Col>
          <Col xs={6} md={2}>
            <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ตัวเลือก</Form.Label>
            <div className='d-flex flex-column gap-1'>
              <Form.Check type='checkbox' id={`req_${field.id}`}   label='บังคับกรอก'       checked={field.required}   onChange={(e) => onChange({ required: e.target.checked })} />
              <Form.Check type='checkbox' id={`score_${field.id}`} label='ใช้คิดคะแนน'     checked={!!field.scorable} onChange={(e) => onChange({ scorable: e.target.checked })} />
              {(field.type === 'select' || field.type === 'checkbox') && (
                <Form.Check type='checkbox' id={`multi_${field.id}`} label='เลือกได้หลายค่า' checked={!!field.multiple} onChange={(e) => onChange({ multiple: e.target.checked })} />
              )}
            </div>
          </Col>

          {isOptionType(field.type) && (
            <Col xs={12}>
              <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ตัวเลือก</Form.Label>
              <div className='d-flex gap-1 mb-2'>
                <Form.Control size='sm' value={optInput} onChange={(e) => setOptInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addOption()} placeholder='พิมพ์แล้วกด Enter' />
                <Button size='sm' variant='outline-primary' onClick={addOption}>เพิ่ม</Button>
              </div>
              <div className='d-flex flex-wrap gap-1'>
                {(field.options || []).map((opt, i) => (
                  <Badge key={i} bg='light' text='dark' style={{ fontSize: 12, cursor: 'pointer', border: '1px solid #ddd', padding: '4px 8px' }}
                    onClick={() => onChange({ options: (field.options || []).filter((_, idx) => idx !== i) })}>
                    {opt.label} ✕
                  </Badge>
                ))}
              </div>
            </Col>
          )}

          <Col xs={12}>
            <div className='d-flex gap-1 justify-content-end'>
              <Button size='sm' variant='outline-secondary' style={{ fontSize: 11 }} onClick={onMoveUp}>↑</Button>
              <Button size='sm' variant='outline-secondary' style={{ fontSize: 11 }} onClick={onMoveDown}>↓</Button>
              <Button size='sm' variant='outline-danger'    style={{ fontSize: 11 }} onClick={onRemove}>ลบ</Button>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  )
}

// ─── FormBuilder Main ─────────────────────────────────────────
export default function FormBuilder() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [loading,      setLoading]      = React.useState(true)
  const [saving,       setSaving]       = React.useState(false)
  const [confirmShow,  setConfirmShow]  = React.useState(false)
  const [formTitle,    setFormTitle]    = React.useState('ฟอร์มลงทะเบียน')
  const [pages,        setPages]        = React.useState<Page[]>([])
  const [activePageId, setActivePageId] = React.useState('')
  const [newFieldType, setNewFieldType] = React.useState<FieldType>('text')
  const [scoreTab,     setScoreTab]     = React.useState('formula')

  const [formulaRules, setFormulaRules] = React.useState<FormulaRule[]>([])
  const [timeRules,    setTimeRules]    = React.useState<TimeRule[]>([])
  const [stringRules,  setStringRules]  = React.useState<StringRule[]>([])
  const [numbRules,    setNumbRules]    = React.useState<NumbRule[]>([])

  const loadDefault = () => {
    const newPages = DEFAULT_PAGES.map((p) => ({
      ...p,
      id: makeId(),
      fields: p.fields.map((f) => ({ ...f, id: makeId() })),
    }))
    setFormTitle('ฟอร์มลงทะเบียน')
    setPages(newPages)
    setActivePageId(newPages[0].id)
    toastSuccess('โหลดฟอร์มเริ่มต้นเรียบร้อย กดบันทึกเพื่อใช้งาน')
  }

  const activePage = pages.find((p) => p.id === activePageId) || pages[0]
  const allFields  = pages.flatMap((p) => p.fields.map((f) => ({ name: f.name, label: f.label, type: f.type })))

  // ─── Load ─────────────────────────────────────────────────────
  React.useEffect(() => {
    ;(async () => {
      try {
        const [formRaw, pointRaw] = await Promise.all([
          dbRead<any>(DB_REGISTER_FORM),
          dbRead<any>(DB_POINT_CONFIG),
        ])

        if (formRaw?.pages?.length) {
          setFormTitle(formRaw.title || 'ฟอร์มลงทะเบียน')
          const hydrated: Page[] = toArr(formRaw.pages).map((p: any) => ({
            id: makeId(), title: p.title || '',
            fields: toArr(p.fields).map((f: any) => ({ ...f, id: makeId() })),
          }))
          setPages(hydrated)
          setActivePageId(hydrated[0]?.id || '')
        } else {
          const pg = makeEmptyPage('ข้อมูลส่วนตัว')
          setPages([pg])
          setActivePageId(pg.id)
        }

        const fr = toArr(pointRaw?.formulaRules)
        const tr = toArr(pointRaw?.timeRules)
        const sr = toArr(pointRaw?.stringRules)
        const nr = toArr(pointRaw?.numbRule)

        setFormulaRules(fr.length ? fr : [{ id: makeId(), label: 'สูตร 1', formula: '', thresholds: defaultTh() }])
        setTimeRules   (tr.length ? tr : [{ dateVar: '', thresholds: defaultTh() }])
        setStringRules (sr.length ? sr : [{ varName: '', mapping: [] }])
        setNumbRules   (nr.length ? nr : [{ varName: '', thresholds: defaultTh() }])

      } catch (e: any) {
        toastDanger(e?.message || 'โหลดแบบฟอร์มไม่สำเร็จ')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // ─── Page handlers ────────────────────────────────────────────
  const addPage = () => {
    const p = makeEmptyPage(`หน้า ${pages.length + 1}`)
    setPages((prev) => [...prev, p])
    setActivePageId(p.id)
  }

  const removePage = (id: string) => {
    if (pages.length === 1) { toastDanger('ต้องมีอย่างน้อย 1 หน้า'); return }
    setPages((prev) => {
      const idx  = prev.findIndex((p) => p.id === id)
      const next = prev.filter((p) => p.id !== id)
      if (id === activePageId) setActivePageId(next[Math.max(0, idx - 1)]?.id || '')
      return next
    })
  }

  const addField = () => {
    if (!activePage) return
    const f = makeEmptyField(newFieldType)
    setPages((prev) => prev.map((p) => p.id !== activePage.id ? p : { ...p, fields: [...p.fields, f] }))
  }

  const updateField = (pageId: string, fieldId: string, patch: Partial<Field>) =>
    setPages((prev) => prev.map((p) =>
      p.id !== pageId ? p : { ...p, fields: p.fields.map((f) => f.id !== fieldId ? f : { ...f, ...patch }) }
    ))

  const removeField = (pageId: string, fieldId: string) =>
    setPages((prev) => prev.map((p) =>
      p.id !== pageId ? p : { ...p, fields: p.fields.filter((f) => f.id !== fieldId) }
    ))

  const moveField = (pageId: string, fieldId: string, dir: -1 | 1) =>
    setPages((prev) => prev.map((p) => {
      if (p.id !== pageId) return p
      const i = p.fields.findIndex((f) => f.id === fieldId)
      const j = i + dir
      if (i < 0 || j < 0 || j >= p.fields.length) return p
      const fields = [...p.fields];
      [fields[i], fields[j]] = [fields[j], fields[i]]
      return { ...p, fields }
    }))

  // ─── Save ─────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true)
    try {
      const formDoc = {
        title: formTitle,
        updatedAt: Date.now(),
        pages: pages.map((p) => ({
          title: p.title,
          fields: p.fields.map(({ id: _id, ...rest }: Field) => rest),
        })),
      }
      const pointDoc = { formulaRules, timeRules, stringRules, numbRule: numbRules }
      await Promise.all([
        dbSet(DB_REGISTER_FORM, formDoc),
        dbSet(DB_POINT_CONFIG, pointDoc),
      ])
      await saveLog(uid, 'update', 'registerform', 'อัปเดตแบบฟอร์ม')
      toastSuccess('บันทึกแบบฟอร์มเรียบร้อย')
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  if (loading) return (
    <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
      <Spinner animation='border' variant='warning' />
    </div>
  )

  return (
    <div className='p-3'>
      <ConfirmAlert
        show={confirmShow}
        title='ยืนยันบันทึกแบบฟอร์ม'
        message='บันทึกการเปลี่ยนแปลงแบบฟอร์มและการคิดคะแนน?'
        confirmLabel={saving ? 'กำลังบันทึก...' : 'ยืนยัน'}
        variant='warning'
        onConfirm={handleSave}
        onCancel={() => setConfirmShow(false)}
      />

      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>ตั้งค่าแบบฟอร์ม</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>{pages.length} หน้า / {pages.reduce((s, p) => s + p.fields.length, 0)} ฟิลด์</div>
        </div>
        <div className='d-flex gap-2'>
          <Button variant='outline-secondary' size='sm' onClick={loadDefault}>
            <i className='fa-solid fa-wand-magic-sparkles me-1' />โหลดฟอร์มเริ่มต้น
          </Button>
          <Button variant='warning' size='sm' disabled={saving} onClick={() => setConfirmShow(true)}>
            <i className='fa-solid fa-floppy-disk me-1' />บันทึกแบบฟอร์ม
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey='form' className='mb-3' style={{ fontSize: 13 }}>

        {/* ─── Tab: โครงสร้างฟอร์ม ──────────────────────── */}
        <Tab eventKey='form' title='โครงสร้างฟอร์ม'>
          <Row className='g-3'>
            <Col xs={12} md={3}>
              <Card className='shadow-sm'>
                <Card.Header style={{ fontSize: 12, fontWeight: 700 }}>
                  หน้าทั้งหมด
                  <Button size='sm' variant='outline-warning' className='ms-2' style={{ fontSize: 10, padding: '1px 6px' }} onClick={addPage}>+ หน้า</Button>
                </Card.Header>
                <ListGroup variant='flush'>
                  {pages.map((p, idx) => (
                    <ListGroup.Item key={p.id} active={activePageId === p.id}
                      style={{ cursor: 'pointer', fontSize: 12, padding: '8px 12px' }}
                      onClick={() => setActivePageId(p.id)}>
                      <div className='d-flex justify-content-between align-items-center'>
                        <span>{idx + 1}. {p.title || '(ไม่มีชื่อ)'}</span>
                        <div className='d-flex gap-1'>
                          <Badge bg='secondary' style={{ fontSize: 9 }}>{p.fields.length}</Badge>
                          <span style={{ fontSize: 10, color: 'red', cursor: 'pointer' }}
                            onClick={(e) => { e.stopPropagation(); removePage(p.id) }}>✕</span>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            </Col>

            <Col xs={12} md={9}>
              {activePage ? (
                <>
                  <div className='d-flex gap-2 align-items-center mb-3 flex-wrap'>
                    <Form.Control size='sm' style={{ maxWidth: 250 }} value={activePage.title}
                      onChange={(e) => setPages((prev) => prev.map((p) => p.id === activePage.id ? { ...p, title: e.target.value } : p))}
                      placeholder='ชื่อหน้า' />
                    <Form.Select size='sm' style={{ maxWidth: 180 }} value={newFieldType} onChange={(e) => setNewFieldType(e.target.value as FieldType)}>
                      {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Form.Select>
                    <Button size='sm' variant='outline-primary' onClick={addField}>
                      <i className='fa-solid fa-plus me-1' />เพิ่มฟิลด์
                    </Button>
                  </div>

                  {activePage.fields.length === 0 && (
                    <Alert variant='light' style={{ fontSize: 13 }}>ยังไม่มีฟิลด์ กดเพิ่มฟิลด์ด้านบน</Alert>
                  )}

                  {activePage.fields.map((f) => (
                    <FieldEditor key={f.id} field={f}
                      onChange={(patch) => updateField(activePage.id, f.id, patch)}
                      onRemove={() => removeField(activePage.id, f.id)}
                      onMoveUp={() => moveField(activePage.id, f.id, -1)}
                      onMoveDown={() => moveField(activePage.id, f.id, 1)}
                    />
                  ))}
                </>
              ) : (
                <Alert variant='info' style={{ fontSize: 13 }}>กรุณาเลือกหน้า</Alert>
              )}
            </Col>
          </Row>
        </Tab>

        {/* ─── Tab: การคิดคะแนน ─────────────────────────── */}
        <Tab eventKey='scoring' title='การคิดคะแนน'>
          <Tabs activeKey={scoreTab} onSelect={(k) => setScoreTab(k || 'formula')} style={{ fontSize: 12 }}>

            {/* Formula */}
            <Tab eventKey='formula' title='สูตรคำนวณ'>
              <div className='p-3'>
                <div className='text-muted mb-3' style={{ fontSize: 12 }}>เขียนสูตร เช่น <code>dailyIncome * 30</code> หรือ <code>now - birthDate</code> (ปี)</div>
                {formulaRules.map((rule, i) => (
                  <Card key={rule.id || i} className='mb-3 shadow-sm'>
                    <Card.Body>
                      <Row className='g-2'>
                        <Col xs={12} md={4}>
                          <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ชื่อกฎ</Form.Label>
                          <Form.Control size='sm' value={rule.label || ''}
                            onChange={(e) => setFormulaRules(formulaRules.map((r, idx) => idx === i ? { ...r, label: e.target.value } : r))} />
                        </Col>
                        <Col xs={12} md={8}>
                          <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>สูตร</Form.Label>
                          <Form.Control size='sm' as='textarea' rows={2} value={rule.formula}
                            onChange={(e) => setFormulaRules(formulaRules.map((r, idx) => idx === i ? { ...r, formula: e.target.value } : r))}
                            placeholder='dailyIncome * 30' />
                        </Col>
                        <Col xs={12}>
                          <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>เงื่อนไขคะแนน</Form.Label>
                          <ThresholdEditor thresholds={toArr(rule.thresholds)}
                            onChange={(t) => setFormulaRules(formulaRules.map((r, idx) => idx === i ? { ...r, thresholds: t } : r))} />
                        </Col>
                      </Row>
                      <Button size='sm' variant='outline-danger' className='mt-2' style={{ fontSize: 11 }}
                        onClick={() => setFormulaRules(formulaRules.filter((_, idx) => idx !== i))}>ลบกฎนี้</Button>
                    </Card.Body>
                  </Card>
                ))}
                <Button size='sm' variant='outline-primary'
                  onClick={() => setFormulaRules([...formulaRules, { id: makeId(), label: `สูตร ${formulaRules.length + 1}`, formula: '', thresholds: defaultTh() }])}>
                  + เพิ่มสูตร
                </Button>
              </div>
            </Tab>

            {/* String */}
            <Tab eventKey='string' title='ตัวเลือก (String)'>
              <div className='p-3'>
                <div className='text-muted mb-3' style={{ fontSize: 12 }}>กำหนดคะแนนตามค่าที่เลือก เช่น สมรส = 10 คะแนน</div>
                {stringRules.map((rule, i) => (
                  <Card key={i} className='mb-3 shadow-sm'>
                    <Card.Body>
                      <Form.Group className='mb-2'>
                        <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ชื่อ Field</Form.Label>
                        <Form.Select size='sm' value={rule.varName}
                          onChange={(e) => setStringRules(stringRules.map((r, idx) => idx === i ? { ...r, varName: e.target.value } : r))}>
                          <option value=''>-- เลือก field --</option>
                          {allFields.map((f) => <option key={f.name} value={f.name}>{f.label} ({f.name})</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>Mapping (ค่า → คะแนน)</Form.Label>
                      {toArr(rule.mapping).map((m, mi) => (
                        <div key={mi} className='d-flex gap-2 align-items-center mb-1'>
                          <Form.Control size='sm' style={{ width: 120 }} value={m.value} placeholder='ค่า'
                            onChange={(e) => setStringRules(stringRules.map((r, idx) => idx === i ? { ...r, mapping: toArr(r.mapping).map((mm, mmi) => mmi === mi ? { ...mm, value: e.target.value } : mm) } : r))} />
                          <span style={{ fontSize: 12 }}>→</span>
                          <Form.Control size='sm' type='number' style={{ width: 80 }} value={m.score} placeholder='คะแนน'
                            onChange={(e) => setStringRules(stringRules.map((r, idx) => idx === i ? { ...r, mapping: toArr(r.mapping).map((mm, mmi) => mmi === mi ? { ...mm, score: Number(e.target.value) } : mm) } : r))} />
                          <Button size='sm' variant='outline-danger' style={{ fontSize: 11 }}
                            onClick={() => setStringRules(stringRules.map((r, idx) => idx === i ? { ...r, mapping: toArr(r.mapping).filter((_, mmi) => mmi !== mi) } : r))}>✕</Button>
                        </div>
                      ))}
                      <div className='d-flex gap-2 mt-1'>
                        <Button size='sm' variant='outline-secondary' style={{ fontSize: 11 }}
                          onClick={() => setStringRules(stringRules.map((r, idx) => idx === i ? { ...r, mapping: [...toArr(r.mapping), { value: '', score: 0 }] } : r))}>+ เพิ่ม</Button>
                        <Button size='sm' variant='outline-danger' style={{ fontSize: 11 }}
                          onClick={() => setStringRules(stringRules.filter((_, idx) => idx !== i))}>ลบกฎนี้</Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
                <Button size='sm' variant='outline-primary' onClick={() => setStringRules([...stringRules, { varName: '', mapping: [] }])}>+ เพิ่มกฎ</Button>
              </div>
            </Tab>

            {/* Number */}
            <Tab eventKey='number' title='ตัวเลข (Number)'>
              <div className='p-3'>
                <div className='text-muted mb-3' style={{ fontSize: 12 }}>กำหนดคะแนนตามค่าตัวเลข</div>
                {numbRules.map((rule, i) => (
                  <Card key={i} className='mb-3 shadow-sm'>
                    <Card.Body>
                      <Form.Group className='mb-2'>
                        <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ชื่อ Field</Form.Label>
                        <Form.Select size='sm' value={rule.varName}
                          onChange={(e) => setNumbRules(numbRules.map((r, idx) => idx === i ? { ...r, varName: e.target.value } : r))}>
                          <option value=''>-- เลือก field --</option>
                          {allFields.filter((f) => f.type === 'number').map((f) => <option key={f.name} value={f.name}>{f.label} ({f.name})</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>เงื่อนไขคะแนน</Form.Label>
                      <ThresholdEditor thresholds={toArr(rule.thresholds)}
                        onChange={(t) => setNumbRules(numbRules.map((r, idx) => idx === i ? { ...r, thresholds: t } : r))} />
                      <Button size='sm' variant='outline-danger' className='mt-2' style={{ fontSize: 11 }}
                        onClick={() => setNumbRules(numbRules.filter((_, idx) => idx !== i))}>ลบกฎนี้</Button>
                    </Card.Body>
                  </Card>
                ))}
                <Button size='sm' variant='outline-primary' onClick={() => setNumbRules([...numbRules, { varName: '', thresholds: defaultTh() }])}>+ เพิ่มกฎ</Button>
              </div>
            </Tab>

            {/* Time */}
            <Tab eventKey='time' title='วันที่ (Time)'>
              <div className='p-3'>
                <div className='text-muted mb-3' style={{ fontSize: 12 }}>กำหนดคะแนนตามอายุของวันที่ เช่น วันเกิด → อายุ</div>
                {timeRules.map((rule, i) => (
                  <Card key={i} className='mb-3 shadow-sm'>
                    <Card.Body>
                      <Form.Group className='mb-2'>
                        <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>ชื่อ Field (date)</Form.Label>
                        <Form.Select size='sm' value={rule.dateVar}
                          onChange={(e) => setTimeRules(timeRules.map((r, idx) => idx === i ? { ...r, dateVar: e.target.value } : r))}>
                          <option value=''>-- เลือก field --</option>
                          {allFields.filter((f) => f.type === 'date').map((f) => <option key={f.name} value={f.name}>{f.label} ({f.name})</option>)}
                        </Form.Select>
                      </Form.Group>
                      <Form.Label style={{ fontSize: 11, fontWeight: 600 }}>เงื่อนไข (ปี ≤ X → คะแนน)</Form.Label>
                      <ThresholdEditor thresholds={toArr(rule.thresholds)}
                        onChange={(t) => setTimeRules(timeRules.map((r, idx) => idx === i ? { ...r, thresholds: t } : r))} />
                      <Button size='sm' variant='outline-danger' className='mt-2' style={{ fontSize: 11 }}
                        onClick={() => setTimeRules(timeRules.filter((_, idx) => idx !== i))}>ลบกฎนี้</Button>
                    </Card.Body>
                  </Card>
                ))}
                <Button size='sm' variant='outline-primary' onClick={() => setTimeRules([...timeRules, { dateVar: '', thresholds: defaultTh() }])}>+ เพิ่มกฎ</Button>
              </div>
            </Tab>

          </Tabs>
        </Tab>
      </Tabs>
    </div>
  )
}